/**
 * CP-133: Browse shop chips (Destination, Cruise line) keep every option
 * visible after one is selected, the same way the static Duration row does.
 *
 * /filters/all cascades on every param it receives, so asking for options
 * with destination=caribbean returns only [caribbean] and the Destination
 * row collapses to Any / Caribbean. Each chip facet's own option list is
 * therefore fetched without that facet's own value (it still honours the
 * other filters, so a Carnival-only shopper sees Carnival's destinations).
 *
 * Results filtering is unchanged: only the option lists are affected, and
 * every option still comes from the API (no invented destinations).
 */

/** Chip facet param -> /filters/all response field it supplies. */
export const CHIP_FACETS = {
  destination: 'destinations',
  cruise_line: 'cruise_lines'
};

/** Copy of `params` without `key` (falsy/empty values are dropped too). */
export function omitParam(params, key) {
  const out = {};
  const src = params && typeof params === 'object' ? params : {};
  Object.keys(src).forEach((k) => {
    if (k === key) return;
    const v = src[k];
    if (v === undefined || v === null || v === '') return;
    out[k] = v;
  });
  return out;
}

/**
 * Extra /filters/all requests needed so each selected chip facet lists its
 * full option set. Returns [{ facet, field, params }]; empty when no chip
 * facet is selected (the main request already lists everything).
 */
export function facetOptionRequests(params) {
  const src = params && typeof params === 'object' ? params : {};
  return Object.keys(CHIP_FACETS)
    .filter((facet) => src[facet] !== undefined && src[facet] !== null && src[facet] !== '')
    .map((facet) => ({
      facet,
      field: CHIP_FACETS[facet],
      params: omitParam(src, facet)
    }));
}

/**
 * Overlay the self-excluded option lists onto the main /filters/all payload.
 * `facetResults` is [{ field, data }] in the same order as the requests.
 * A failed or malformed facet response keeps the main payload's list.
 */
export function mergeFacetOptions(mainData, facetResults) {
  const merged = { ...(mainData && typeof mainData === 'object' ? mainData : {}) };
  (Array.isArray(facetResults) ? facetResults : []).forEach((result) => {
    if (!result || !result.field || !result.data) return;
    const list = result.data[result.field];
    if (!Array.isArray(list) || list.length === 0) return;
    merged[result.field] = list;
  });
  return merged;
}
